import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { getActuatorNumberOfChannels, getActuatorState, getApplianceCapacityPerChannel, getConnectedApplianceIds, getUsedActuatorChannels, normalizeActuatorChannelConnections } from '../../Functions/ActuatorChannels';
import { getDetectorConnectedActuatorIds } from '../../Functions/DetectorConnections';
import { ActuatorStateI, AppliancesStateI, AppStateI, DetectorStateI, DevicesStateI, HoveredItemI } from '../types/app';
import { EmailPasswordI } from '../types/auth';
import { DeviceI } from '../types/controllers';

const initAppState: AppStateI = {
  controllerType: '',
  hasUnsavedChanges: false,
  lastSavedConfigurationSignature: null,
  leftMenuShown: false,
  rightMenuShown: false,
  bottomBarOpen: false,
  selectedPage: '',
  isLoading: false,
  isEstablishingConnection: false,
  isConnected: false,
  selectingDevice: false,
  showApplianceDetail: false,
  showDeviceDetail: false,
  showActuatorDetail: false,
  showThermostatDetail: false,
  showDetectorDetail: false,
  showAddDevicePopup: false,
  hoveredItem: null,
  showSaveDetail: false,
  configurationResetRevision: 0,
  floorplanImage: null,
  appliances: [] as AppliancesStateI[],
  devices: [] as DevicesStateI[],
  actuators: [] as ActuatorStateI[],
  detectors: [] as DetectorStateI[]
};

const getApplianceState = (actuator: number | undefined) => actuator !== undefined ? 'ok' : 'error';

const getSyncedApplianceState = (
  appliance: AppliancesStateI,
  controllers: DeviceI[],
  actuators: ActuatorStateI[]
) => {
  if (appliance.actuator !== undefined || appliance.applianceType !== 'hvac') {
    return getApplianceState(appliance.actuator);
  }

  const directlyConnectedThermostat = controllers.some((controller) =>
    controller.type === 'thermostat'
    && (controller.thermostatRequiredActuator === null || controller.thermostatRequiredActuator === undefined)
    && controller.iconsData.some((slot) => (slot.applianceId ?? []).includes(appliance.id))
  );

  if (directlyConnectedThermostat) {
    return 'ok' as const;
  }

  const requiredActuatorConnected = controllers.some((controller) => {
    const requiredActuator = controller.thermostatRequiredActuator;
    const connectedActuatorIds = controller.connectedActuatorIds
      ?? (controller.connectedActuator === undefined ? [] : [controller.connectedActuator]);
    if (controller.type !== 'thermostat' || !requiredActuator || connectedActuatorIds.length === 0) {
      return false;
    }

    return connectedActuatorIds.some((actuatorId) => {
      const connectedActuator = actuators.find((actuator) => actuator.id === actuatorId);
      return appliance.actuator === actuatorId && connectedActuator?.type === requiredActuator;
    });
  });

  return requiredActuatorConnected ? 'warning' as const : 'error' as const;
};

const getControllerLinkedActuatorIds = (controllers: DeviceI[]) => {
  const linkedActuatorIds = new Set<number>();

  controllers.forEach((controller) => {
    controller.iconsData.forEach((slot) => {
      slot.connectedActuatorChannels?.forEach((link) => linkedActuatorIds.add(link.actuatorId));
      if (slot.connectedActuatorId !== undefined) {
        linkedActuatorIds.add(slot.connectedActuatorId);
      }
    });
  });

  return linkedActuatorIds;
};

const getActuatorStatus = (
  actuator: ActuatorStateI,
  connectedAppliances: number[],
  controllers: DeviceI[],
  detectors: DetectorStateI[]
) => {
  const hasRequiredThermostatConnection = controllers.some((controller) =>
    controller.type === 'thermostat'
    && controller.thermostatRequiredActuator !== null
    && controller.thermostatRequiredActuator !== undefined
    && (controller.connectedActuatorIds ?? [controller.connectedActuator]).includes(actuator.id)
  );
  const hasControllerConnection = actuator.linkedToController === true
    || actuator.builtIn === true
    || getControllerLinkedActuatorIds(controllers).has(actuator.id ?? -1)
    || hasRequiredThermostatConnection;
  const hasDetectorConnection = detectors.some((detector) =>
    detector.connectedActuatorIds?.includes(actuator.id ?? -1)
  );
  const hasAllApplianceChannelsUsed = getUsedActuatorChannels(actuator) >= getActuatorNumberOfChannels(actuator);

  return getActuatorState(
    hasAllApplianceChannelsUsed,
    hasControllerConnection || hasDetectorConnection
  );
};

const appSlice = createSlice({
  name: 'app',
  initialState: initAppState,
  reducers: {
    setControllerType: (state, action: PayloadAction<string>) => {
      state.controllerType = action.payload;
    },
    setHasUnsavedChanges: (state, action: PayloadAction<boolean>) => {
      state.hasUnsavedChanges = action.payload;
    },
    setSavedConfigurationSignature: (state, action: PayloadAction<string>) => {
      state.lastSavedConfigurationSignature = action.payload;
      state.hasUnsavedChanges = false;
    },
    resetUnsavedChangesTracking: (state) => {
      state.lastSavedConfigurationSignature = null;
      state.hasUnsavedChanges = false;
    },
    toggleLeftMenu: (state) => {
      state.leftMenuShown = !state.leftMenuShown;
    },
    setLeftMenuShown: (state, action: PayloadAction<boolean>) => {
      state.leftMenuShown = action.payload;
    },
    toggleRightMenu: (state) => {
      state.rightMenuShown = !state.rightMenuShown;
    },
    setBottomBarOpen: (state, action: PayloadAction<boolean>) => {
      state.bottomBarOpen = action.payload;
    },
    setSelectedTab: (state, action: PayloadAction<string>) => {
      state.selectedPage = action.payload;
    },
    setIsLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    // eslint-disable-next-line 
    startConnecting: (state, action: PayloadAction<EmailPasswordI>) => {
      // action is present because of passing arguments to wssMiddleware 
      state.isEstablishingConnection = true;
    },
    connectionEstablished: (state) => {
      state.isConnected = true;
      state.isEstablishingConnection = true;
    },
    setAppliances: (state, action: PayloadAction<AppliancesStateI[]>) => {
      state.appliances = action.payload.map((incoming) => {
        const existing = state.appliances.find((a) => a.id === incoming.id);
        const connectedTo = incoming.connectedTo ?? existing?.connectedTo ?? [];
        const actuator = 'actuator' in incoming ? incoming.actuator : (existing?.actuator ?? undefined);
        const x = incoming.x ?? existing?.x;
        const y = incoming.y ?? existing?.y;
        return {
          ...incoming,
          connectedTo,
          actuator,
          x,
          y,
          state: getApplianceState(actuator)
        };
      });
    },
    addAppliance: (state, action: PayloadAction<AppliancesStateI>) => {
      state.appliances.push(action.payload);
    },
    setActuators: (state, action: PayloadAction<ActuatorStateI[]>) => {
      state.actuators = action.payload.map((incoming) => {
        const existing = state.actuators.find((a) => a.id === incoming.id);
        const numberOfChannels = getActuatorNumberOfChannels({
          ...existing,
          ...incoming
        });
        const connectedAppliancesByChannel = normalizeActuatorChannelConnections(
          incoming.connectedAppliancesByChannel ?? existing?.connectedAppliancesByChannel,
          numberOfChannels,
          incoming.connectedAppliances ?? existing?.connectedAppliances ?? [],
          getApplianceCapacityPerChannel({ ...existing, ...incoming })
        );
        const connectedAppliances = getConnectedApplianceIds({
          ...existing,
          ...incoming,
          numberOfChannels,
          connectedAppliancesByChannel
        });
        const x = incoming.x ?? existing?.x;
        const y = incoming.y ?? existing?.y;
        const maximumApplianceConnections = incoming.maximumApplianceConnections
          ?? existing?.maximumApplianceConnections
          ?? numberOfChannels;

        return {
          ...incoming,
          connectedAppliances,
          connectedAppliancesByChannel,
          x,
          y,
          numberOfChannels,
          maximumApplianceConnections,
          linkedToController: incoming.linkedToController ?? existing?.linkedToController ?? incoming.builtIn === true,
          state: getActuatorStatus(
            {
              ...incoming,
              builtIn: incoming.builtIn ?? existing?.builtIn,
              numberOfChannels,
              connectedAppliances,
              connectedAppliancesByChannel,
              linkedToController: incoming.linkedToController
                ?? existing?.linkedToController
                ?? incoming.builtIn === true
            },
            connectedAppliances,
            [],
            state.detectors
          )
        };
      });
    },
    addActuator: (state, action: PayloadAction<ActuatorStateI>) => {
      state.actuators.push(action.payload);
    },
    setActuatorPosition: (state, action: PayloadAction<{ id: number; x: number; y: number }>) => {
      const actuator = state.actuators.find((item) => item.id === action.payload.id);
      if (actuator) {
        actuator.x = action.payload.x;
        actuator.y = action.payload.y;
      }
    },
    setActuatorControllerLinks: (state, action: PayloadAction<DeviceI[]>) => {
      state.actuators = state.actuators.map((actuator) => ({
        ...actuator,
        linkedToController: actuator.builtIn === true
          || getControllerLinkedActuatorIds(action.payload).has(actuator.id ?? -1)
          || action.payload.some((controller) =>
            controller.type === 'thermostat'
            && controller.thermostatRequiredActuator !== null
            && controller.thermostatRequiredActuator !== undefined
            && (controller.connectedActuatorIds ?? [controller.connectedActuator]).includes(actuator.id)
          ),
        state: getActuatorStatus(
          {
            ...actuator,
            linkedToController: actuator.builtIn === true
              || getControllerLinkedActuatorIds(action.payload).has(actuator.id ?? -1)
              || action.payload.some((controller) =>
                controller.type === 'thermostat'
                && controller.thermostatRequiredActuator !== null
                && controller.thermostatRequiredActuator !== undefined
                && (controller.connectedActuatorIds ?? [controller.connectedActuator]).includes(actuator.id)
              )
          },
          getConnectedApplianceIds(actuator),
          [],
          state.detectors
        )
      }));
    },
    setDetectors: (state, action: PayloadAction<DetectorStateI[]>) => {
      state.detectors = action.payload.map((incoming) => {
        const connectedActuatorIds = getDetectorConnectedActuatorIds(incoming);
        return {
          ...incoming,
          connectedActuatorIds,
          state: connectedActuatorIds.length > 0 ? 'ok' : 'error'
        };
      });

      state.actuators = state.actuators.map((actuator) => ({
        ...actuator,
        state: getActuatorStatus(
          actuator,
          getConnectedApplianceIds(actuator),
          [],
          state.detectors
        )
      }));
    },
    addDetector: (state, action: PayloadAction<DetectorStateI>) => {
      state.detectors.push(action.payload);
    },
    connectApplianceToController: (state, action: PayloadAction<{ applianceId: number, controllerId: number }>) => {
      const appliance = state.appliances.find((a) => a.id === action.payload.applianceId);
      if(!appliance){
        return;
      }

      const current = appliance.connectedTo ?? [];
      if(!current.includes(action.payload.controllerId)){
        appliance.connectedTo = [...current, action.payload.controllerId];
      }else{
        appliance.connectedTo = current;
      }

      appliance.state = getApplianceState(appliance.actuator ?? undefined);
    },
    disconnectApplianceFromController: (state, action: PayloadAction<{ applianceId: number, controllerId: number }>) => {
      const appliance = state.appliances.find((a) => a.id === action.payload.applianceId);
      if(!appliance){
        return;
      }

      const current = appliance.connectedTo ?? [];
      appliance.connectedTo = current.filter((id) => id !== action.payload.controllerId);
      appliance.state = getApplianceState(appliance.actuator ?? undefined);
    },
    disconnectControllerFromAllAppliances: (state, action: PayloadAction<number>) => {
      const controllerId = action.payload;
      state.appliances.forEach((appliance) => {
        const current = appliance.connectedTo ?? [];
        const next = current.filter((id) => id !== controllerId);
        appliance.connectedTo = next;
        appliance.state = getApplianceState(appliance.actuator ?? undefined);
      });
    },
    syncApplianceControllerConnections: (state, action: PayloadAction<DeviceI[]>) => {
      const controllers = action.payload;
      const applianceConnectionsByController = new Map<number, Set<number>>();

      controllers.forEach((controller) => {
        controller.iconsData.forEach((slot) => {
          (slot.applianceId ?? []).forEach((applianceId) => {
            const existing = applianceConnectionsByController.get(applianceId);
            if (existing) {
              existing.add(controller.id);
            } else {
              applianceConnectionsByController.set(applianceId, new Set([controller.id]));
            }
          });
        });
      });

      state.appliances = state.appliances.map((appliance) => {
        const nextConnectedTo = Array.from(applianceConnectionsByController.get(appliance.id) ?? []);
        return {
          ...appliance,
          connectedTo: nextConnectedTo,
          state: getSyncedApplianceState(
            { ...appliance, connectedTo: nextConnectedTo },
            controllers,
            state.actuators
          )
        };
      });
    },
    setSelectingDevice: (state, action: PayloadAction<boolean>) => {
      state.selectingDevice = action.payload;
    },
    setShowApplianceDetail: (state, action: PayloadAction<boolean>) => {
      state.showApplianceDetail = action.payload;
    },
    setShowDeviceDetail: (state, action: PayloadAction<boolean>) => {
      state.showDeviceDetail = action.payload;
    },
    setShowActuatorDetail: (state, action: PayloadAction<boolean>) => {
      state.showActuatorDetail = action.payload;
      if (action.payload) {
        state.actuators = state.actuators.map((actuator) => ({
          ...actuator,
          state: getActuatorStatus(
            actuator,
            getConnectedApplianceIds(actuator),
            [],
            state.detectors
          )
        }));
      }
    },
    setShowSaveDetail: (state, action: PayloadAction<boolean>) => {
      state.showSaveDetail = action.payload;
    },
    setShowThermostatDetail: (state, action: PayloadAction<boolean>) => {
      state.showThermostatDetail = action.payload;
    },
    setShowDetectorDetail: (state, action: PayloadAction<boolean>) => {
      state.showDetectorDetail = action.payload;
      if (action.payload) {
        state.actuators = state.actuators.map((actuator) => ({
          ...actuator,
          state: getActuatorStatus(
            actuator,
            getConnectedApplianceIds(actuator),
            [],
            state.detectors
          )
        }));
      }
    },
    setHoveredItem: (state, action: PayloadAction<HoveredItemI | null>) => {
      state.hoveredItem = action.payload;
    },
    setApplianceLabel: (state, action: PayloadAction<{ id: number; label: string }>) => {
      const appliance = state.appliances.find((a) => a.id === action.payload.id);
      if (appliance) {
        appliance.label = action.payload.label;
      }
    },
    resetConfiguration: (state) => {
      state.appliances = [] as AppliancesStateI[];
      state.devices = [] as DevicesStateI[];
      state.actuators = [] as ActuatorStateI[];
      state.detectors = [] as DetectorStateI[];
      state.hoveredItem = null;
      state.showDeviceDetail = false;
      state.showSaveDetail = false;
      state.selectingDevice = false;
      state.configurationResetRevision += 1;
      state.lastSavedConfigurationSignature = null;
      state.hasUnsavedChanges = false;
    },
    setShowAddDevicePopup: (state, action: PayloadAction<boolean>) => {
      state.showAddDevicePopup = action.payload;
    },
    setFloorplanImage: (state, action: PayloadAction<string | null>) => {
      state.floorplanImage = action.payload;
    }
  }
});

export const {
  setControllerType,
  setHasUnsavedChanges,
  setSavedConfigurationSignature,
  resetUnsavedChangesTracking,
  toggleLeftMenu,
  setLeftMenuShown,
  toggleRightMenu,
  setBottomBarOpen,
  setSelectedTab,
  setIsLoading,
  startConnecting,
  connectionEstablished,
  setAppliances,
  addAppliance,
  setActuators,
  addActuator,
  setActuatorPosition,
  setActuatorControllerLinks,
  setDetectors,
  addDetector,
  connectApplianceToController,
  disconnectApplianceFromController,
  disconnectControllerFromAllAppliances,
  syncApplianceControllerConnections,
  setSelectingDevice,
  setShowApplianceDetail,
  setShowDeviceDetail,
  setShowActuatorDetail,
  setShowSaveDetail,
  setShowThermostatDetail,
  setShowDetectorDetail,
  setHoveredItem,
  setApplianceLabel,
  resetConfiguration,
  setShowAddDevicePopup,
  setFloorplanImage
} = appSlice.actions;

export default appSlice.reducer;
